import React, { useState } from 'react';
import { 
  getRecommendedFeatures,
  calculateFeatureTotalImpact,
  RoosterFeature 
} from '../../../data/roosterFeatures';
import roosterIcon from '../../../assets/images/rooster.ico';

interface FeatureRecommendationsProps {
  tasComponents: {
    schedulingVelocity: number;
    schedulingEfficiency: number;
    candidateExperience: number;
    schedulingIntegrity: number;
    schedulingUtilization: number;
  };
  currentTAS: number;
  projectedTAS: number;
  tasImprovement: number;
}

export const FeatureRecommendations: React.FC<FeatureRecommendationsProps> = ({
  tasComponents,
  currentTAS,
  projectedTAS,
  tasImprovement
}) => {
  const [showDetails, setShowDetails] = useState(false);
  
  // Get recommended features based on weak components
  const recommendedFeatures = getRecommendedFeatures(tasComponents, 8);
  
  // Calculate implementation phases based on TAS improvement target
  const getImplementationPhase = (index: number): 'immediate' | 'phase1' | 'phase2' | 'phase3' => {
    if (index < 2) return 'immediate';
    if (index < 4) return 'phase1';
    if (index < 6) return 'phase2';
    return 'phase3';
  };
  
  const phaseInfo = {
    immediate: { label: 'Quick Wins', timeline: 'Week 1-2', color: 'bg-emerald-100 text-emerald-700' },
    phase1: { label: 'Phase 1', timeline: 'Week 3-4', color: 'bg-sky-100 text-sky-700' },
    phase2: { label: 'Phase 2', timeline: '2mo', color: 'bg-cyan-100 text-cyan-700' },
    phase3: { label: 'Phase 3', timeline: '3mo', color: 'bg-teal-100 text-teal-700' }
  };
  
  // Identify the weakest components
  const weakestComponents = Object.entries(tasComponents)
    .sort(([, a], [, b]) => a - b)
    .slice(0, 3)
    .map(([component, score]) => ({
      component: component as keyof typeof tasComponents,
      score,
      gap: 80 - score // Gap to "Good" level
    }));
  
  const componentNames = {
    schedulingVelocity: 'Scheduling Velocity (SV)',
    schedulingEfficiency: 'Scheduling Efficiency (SE)',
    candidateExperience: 'Candidate Experience (CE)',
    schedulingIntegrity: 'Scheduling Integrity (SI)',
    schedulingUtilization: 'Scheduling Utilization (SU)'
  };

  const renderFeatureCard = (feature: RoosterFeature, index: number) => {
    const phase = getImplementationPhase(index);
    const phaseData = phaseInfo[phase];
    const totalImpact = calculateFeatureTotalImpact(feature);
    
    // Calculate expected TAS improvement from this feature
    const expectedImprovement = Math.round(totalImpact * 0.3); // Rough estimate
    
    return (
      <div 
        key={feature.id} 
        className="bg-white rounded-lg p-4 border border-gray-200 hover:shadow-lg hover:border-sky-300 transition-all cursor-pointer group"
        title="View in TAS Score section for detailed analysis"
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center">
            <span className="text-2xl mr-3">{feature.icon}</span>
            <div>
              <h4 className="font-semibold text-gray-900 group-hover:text-sky-700 transition-colors">{feature.name}</h4>
              <span className={`inline-block mt-1 px-2 py-1 rounded text-xs font-medium ${phaseData.color}`}>
                {phaseData.label} • {phaseData.timeline}
              </span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold text-sky-600">+{expectedImprovement}</div>
            <div className="text-xs text-gray-500">TAS Points</div>
          </div>
        </div>
        
        <p className="text-sm text-gray-600 mb-3">{feature.whyItMatters}</p>
        
        {/* Impact breakdown */}
        <div className="bg-gray-50 rounded p-2 mb-3">
          <div className="text-xs font-medium text-gray-700 mb-1">Impact Areas:</div>
          <div className="flex flex-wrap gap-1">
            {Object.entries(feature.impacts).map(([component, impact]) => {
              if (!impact) return null;
              const componentName = componentNames[component as keyof typeof componentNames];
              const shortName = componentName.match(/\(([^)]+)\)/)?.[1] || '';
              
              return (
                <span 
                  key={component}
                  className="inline-flex items-center px-2 py-1 rounded text-xs bg-white border border-gray-200"
                >
                  <span className="font-medium mr-1">{shortName}</span>
                  <span className={`${
                    impact >= 70 ? 'text-emerald-600' :
                    impact >= 40 ? 'text-sky-600' :
                    'text-gray-600'
                  }`}>
                    {impact}%
                  </span>
                </span>
              );
            })}
          </div>
        </div>
        
        {/* Implementation info */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>
            Complexity: <span className={`font-medium ${
              feature.implementationComplexity === 'low' ? 'text-emerald-600' :
              feature.implementationComplexity === 'medium' ? 'text-yellow-600' :
              'text-red-600'
            }`}>{feature.implementationComplexity}</span>
          </span>
          <span>Time to Value: <span className="font-medium">{feature.timeToValue}</span></span>
        </div>
      </div>
    );
  };

  return (
    <div className="mt-8">
      <div className="bg-gradient-to-r from-sky-50 to-cyan-50 rounded-lg p-6 border border-sky-100">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-800 flex items-center">
              <img src={roosterIcon} alt="Rooster" className="w-6 h-6 mr-2" />
              Your Personalized Feature Roadmap
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Based on your current scores, these features will have the biggest impact
            </p>
          </div>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-sm text-sky-600 hover:text-sky-700 font-medium"
          >
            {showDetails ? 'Hide Details' : 'Show Details'}
          </button>
        </div>
        
        {/* Weak Components Analysis */}
        <div className="bg-white rounded-lg p-4 mb-4 border border-gray-200">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Areas for Improvement</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {weakestComponents.map(({ component, score, gap }) => (
              <div key={component} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{componentNames[component]}</span>
                <div className="flex items-center">
                  <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                    <div 
                      className="bg-red-500 h-2 rounded-full transition-all"
                      style={{ width: `${score}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-gray-700">{Math.round(score)}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Recommended Features Grid */}
        {showDetails && (
          <div className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {recommendedFeatures.map((feature, index) => renderFeatureCard(feature, index))}
            </div>
            
            {/* Implementation Summary */}
            <div className="mt-6 bg-white rounded-lg p-4 border border-gray-200">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Implementation Summary</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(phaseInfo).map(([phase, info]) => {
                  const featuresInPhase = recommendedFeatures.filter((_, index) => 
                    getImplementationPhase(index) === phase
                  );
                  
                  if (featuresInPhase.length === 0) return null;
                  
                  return (
                    <div key={phase} className="text-center">
                      <div className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-2 ${info.color}`}>
                        {info.label}
                      </div>
                      <div className="text-lg font-bold text-gray-900">{featuresInPhase.length}</div>
                      <div className="text-xs text-gray-500">features</div>
                      <div className="text-xs text-gray-600 mt-1">{info.timeline}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        
        {/* Quick Summary if details are hidden */}
        {!showDetails && (
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">
                  <span className="font-semibold text-gray-900">{recommendedFeatures.length} features</span> recommended 
                  to achieve your <span className="font-semibold text-sky-600">+{tasImprovement} point</span> improvement goal
                </p>
              </div>
              <div className="flex -space-x-2">
                {recommendedFeatures.slice(0, 5).map((feature, index) => (
                  <div 
                    key={feature.id}
                    className="w-8 h-8 rounded-full bg-white border-2 border-white flex items-center justify-center text-sm shadow-sm"
                    title={feature.name}
                  >
                    {feature.icon}
                  </div>
                ))}
                {recommendedFeatures.length > 5 && (
                  <div className="w-8 h-8 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center text-xs font-medium text-gray-600 shadow-sm">
                    +{recommendedFeatures.length - 5}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};